/**
 * FirstCommentLimits — per-platform character cap row for the
 * composer's First Comment textarea.
 *
 * Mirrors backend `_FIRST_COMMENT_LIMITS` in
 * `backend/app/services/validation/preflight.py`. When those caps
 * change, update both tables.
 *
 * Only platforms that support a "first comment" (Meta Graph API
 * comment replies, LinkedIn UGC comments, TikTok first-comment API)
 * are listed. Platforms that ignore first_comment at publish time
 * never render here so users don't see spurious warnings.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle } from 'lucide-react';

const LIMITS: Record<string, number> = {
  instagram: 2200,
  facebook: 8000,
  linkedin: 1250,
  tiktok: 150,
  threads: 500,
  youtube: 10000,
  pinterest: 500,
  bluesky: 300,
};

interface Props {
  id: string;
  text: string;
  selectedPlatforms: string[];
}

const FirstCommentLimits: React.FC<Props> = ({ id, text, selectedPlatforms }) => {
  const { t } = useTranslation();
  const len = text.length;

  const rows = selectedPlatforms
    .filter((p) => LIMITS[p] !== undefined)
    .map((platform) => {
      const max = LIMITS[platform];
      const over = len > max;
      return { platform, max, over };
    });

  if (rows.length === 0) {
    return null;
  }

  const anyOver = rows.some((r) => r.over);

  return (
    <div
      id={id}
      role="status"
      aria-live="polite"
      className={`mt-1.5 flex flex-wrap items-center gap-2 text-[11px] ${
        anyOver ? 'text-red-600' : 'text-slate-500'
      }`}
    >
      {anyOver ? (
        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
      ) : (
        <CheckCircle className="h-3 w-3 text-emerald-500" aria-hidden="true" />
      )}
      <span className="tabular-nums">
        {t('comp_first_comment_char_count', '{{n}} chars', { n: len })}
      </span>
      <span aria-hidden="true">·</span>
      {rows.map((r, i) => (
        <span
          key={r.platform}
          className={`tabular-nums ${
            r.over ? 'text-red-600 font-semibold' : 'text-slate-500'
          }`}
          title={t(
            'comp_first_comment_platform_limit_title',
            '{{p}} first-comment limit: {{max}}',
            { p: r.platform, max: r.max }
          )}
        >
          {r.platform}: {len}/{r.max}
          {i < rows.length - 1 && <span aria-hidden="true"> ·</span>}
        </span>
      ))}
    </div>
  );
};

export default FirstCommentLimits;
